import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { EASE_OUT_NOTICE } from "@/lib/motion";
import { Link } from "react-router-dom";
import { useTranslation } from "@/i18n/I18nProvider";
import { getAnonId, readConsent, setConsent } from "@/lib/anonId";
import { maybeLinkAnonId } from "@/services/authStore";

/**
 * The one-time anonymous-ID consent card (ePrivacy: a persistent identifier —
 * even a first-party one — needs opt-in). Deliberately the opposite of a
 * cookie-banner monstrosity: a small crisp-edge card pinned to the bottom
 * corner, nothing blocked, no re-prompts once answered, and the feed is fully
 * usable if it's ignored forever.
 *
 * Non-modal by design — `role="region"`, no focus steal, no trap. It renders
 * a beat after mount so it never competes with the feed's first paint.
 */
export function ConsentNotice() {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (readConsent() !== "unset") return;
    const timer = window.setTimeout(() => setVisible(true), 1200);
    return () => window.clearTimeout(timer);
  }, []);

  const answer = (granted: boolean) => {
    setConsent(granted);
    setVisible(false);
    if (granted) {
      // Mint immediately — "remember this device" starts the moment the
      // visitor allows it, not at some later first use.
      getAnonId();
      // Already signed in when consent lands (e.g. a Google user's first
      // visit after the redirect) → link the fresh id right away.
      maybeLinkAnonId();
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.aside
          initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 16 }}
          animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={EASE_OUT_NOTICE}
          role="region"
          aria-label={t("CONSENT_ARIA")}
          className="fixed inset-x-3 z-[70] rounded-2xl border border-white/15 light:border-black/15 bg-zinc-950/95 light:bg-white/95 p-4 shadow-[0_0_0_1px_#000,0_16px_40px_-20px_#000] backdrop-blur sm:inset-x-auto sm:left-5 sm:max-w-sm"
          style={{ bottom: "max(0.75rem, env(safe-area-inset-bottom, 0px))" }}
        >
          <p className="text-pretty text-[13px] leading-relaxed text-zinc-300 light:text-zinc-700">
            {t("CONSENT_BODY")}{" "}
            <Link
              to="/privacy"
              className="underline decoration-zinc-600 underline-offset-2 transition hover:text-zinc-100 light:hover:text-zinc-900"
            >
              {t("CONSENT_MORE")}
            </Link>
          </p>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={() => answer(true)}
              className="inline-flex items-center justify-center rounded-full bg-paper light:bg-ink px-4 py-1.5 text-xs font-semibold text-ink light:text-paper transition hover:bg-white light:hover:bg-zinc-800"
            >
              {t("CONSENT_ACCEPT")}
            </button>
            <button
              type="button"
              onClick={() => answer(false)}
              className="inline-flex items-center justify-center rounded-full border border-white/[0.12] light:border-black/10 bg-zinc-900/60 light:bg-white/70 px-4 py-1.5 text-xs font-medium text-zinc-300 light:text-zinc-700 transition hover:border-white/30 light:hover:border-black/30 hover:text-zinc-100 light:hover:text-zinc-900"
            >
              {t("CONSENT_DECLINE")}
            </button>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
